import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Send, Loader2, CheckCircle2, CircleSlash, Eye, Lock, PenLine, RotateCcw, MessageSquareText, Download, Link as LinkIcon, ExternalLink, History,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { txError } from '@/lib/txErrors';
import { toast } from 'sonner';

const CATEGORY_ORDER = ['responsibilities', 'pending_matters', 'access_equipment', 'knowledge_transfer', 'other'];

function NoteDialog({ open, onOpenChange, title, description, onSubmit, submitLabel, testId }) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setNote(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${testId}-note`} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button
            disabled={busy || note.trim().length < 5}
            onClick={async () => { setBusy(true); try { await onSubmit(note.trim()); onOpenChange(false); } finally { setBusy(false); } }}
            data-testid={`${testId}-submit`}
          >
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HrRecordDialog({ open, onOpenChange, caseId, onDone }) {
  const { t, i18n } = useTranslation();
  const es = i18n.language?.startsWith('es');
  const [instrument, setInstrument] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setAnswers({}); return; }
    api.get(`/client/talentexit/cases/${caseId}`)
      .then(() => api.get('/client/talentexit/checklist-template'))
      .catch(() => {});
    // Instrument questions come from the assignment engine; fetch the published
    // standard instrument via the case interview metadata is server-side. For the
    // HR form we load the instrument through the responses endpoint contract:
    api.get(`/client/talentexit/instrument`).then((r) => setInstrument(r.data)).catch(() => setInstrument(null));
  }, [open, caseId]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.entries(answers).forEach(([qid, v]) => { payload[qid] = v; });
      await api.post(`/client/talentexit/cases/${caseId}/interview/record`, { answers: payload });
      toast.success(t('talentexit.interview.recordedOk', { defaultValue: 'Interview recorded.' }));
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(txError(t, e)); } finally { setBusy(false); }
  };

  const questions = instrument?.questions || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="tx-hr-dialog">
        <DialogHeader>
          <DialogTitle>{t('talentexit.interview.recordTitle', { defaultValue: 'Record HR-led exit interview' })}</DialogTitle>
          <DialogDescription>{t('talentexit.interview.recordDesc', { defaultValue: 'Capture the responses given by the departing employee. The record will show it was HR-recorded, by whom, and when.' })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {questions.map((q) => (
            <div key={q.id} className="space-y-1.5">
              <Label className="leading-snug">{es ? q.prompt : (q.prompt_en || q.prompt)}</Label>
              {q.type === 'single_choice' ? (
                <div className="flex flex-wrap gap-2">
                  {(q.options || []).map((o) => (
                    <Button key={o.id} type="button" size="sm"
                      variant={answers[q.id]?.option_id === o.id ? 'default' : 'outline'}
                      onClick={() => setAnswers((a) => ({ ...a, [q.id]: { option_id: o.id } }))}
                      data-testid={`tx-hr-opt-${o.id}`}>
                      {es ? o.label : (o.label_en || o.label)}
                    </Button>
                  ))}
                </div>
              ) : (
                <Textarea rows={2} value={answers[q.id]?.text || ''}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: { text: e.target.value } }))} />
              )}
            </div>
          ))}
          {questions.length === 0 && <p className="text-sm text-muted-foreground">{t('talentexit.interview.noInstrument', { defaultValue: 'Instrument unavailable.' })}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={submit} disabled={busy || Object.keys(answers).length === 0} data-testid="tx-hr-submit">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {t('talentexit.interview.recordSubmit', { defaultValue: 'Save interview' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TalentExitResponsesView({ data }) {
  const { t, i18n } = useTranslation();
  const es = i18n.language?.startsWith('es');
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="tx-responses-view">
      <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
        <Badge variant="outline">{data.mode === 'hr_recorded'
          ? t('talentexit.interview.modeHr', { defaultValue: 'HR-recorded' })
          : t('talentexit.interview.modePortal', { defaultValue: 'Completed in portal' })}</Badge>
        {data.recorded_by_name && <span>{t('talentexit.interview.recordedBy', { defaultValue: 'Recorded by' })}: {data.recorded_by_name}</span>}
        <span>v{data.version_number}</span>
      </div>
      {(data.items || []).map((it) => (
        <div key={it.question_id} className="rounded border border-border/60 p-3">
          <p className="text-sm font-medium">{es ? it.prompt_es : it.prompt_en}</p>
          <div className="mt-1.5 text-sm text-muted-foreground">
            {(it.selected_options || []).map((o, i) => (
              <Badge key={i} variant="outline" className="mr-1.5">{es ? o.label_es : o.label_en}</Badge>
            ))}
            {it.text && <p className="whitespace-pre-wrap mt-1">{it.text}</p>}
            {!it.text && (it.selected_options || []).length === 0 && <span>—</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function TalentExitCaseDetail({ caseId, onClose, adminMode = false, companyId = null }) {
  const { t, i18n } = useTranslation();
  const es = i18n.language?.startsWith('es');
  const base = adminMode ? `/admin/talentexit/${companyId}/cases/${caseId}` : `/client/talentexit/cases/${caseId}`;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [hrOpen, setHrOpen] = useState(false);
  const [naItem, setNaItem] = useState(null);
  const [responses, setResponses] = useState(null);
  const [showResponses, setShowResponses] = useState(false);
  const [closureNote, setClosureNote] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [history, setHistory] = useState([]);

  const load = useCallback(() => {
    api.get(base).then((r) => {
      setData(r.data);
      setEvidenceUrl(r.data?.evidence_link?.url || '');
    }).catch((e) => { toast.error(txError(t, e)); onClose(); });
    api.get(`${base}/history`).then((r) => setHistory(r.data?.events || [])).catch(() => {});
  }, [base, onClose]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const iv = data.interview || {};
  const closed = data.status === 'closed';
  const ivResolved = ['completed', 'recorded', 'waived'].includes(iv.status);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (e) { toast.error(txError(t, e)); }
    finally { setBusy(false); }
  };

  const patchItem = (item, status, note) =>
    act(() => api.patch(`/client/talentexit/cases/${caseId}/checklist/${item.id}`, { status, note }));

  const downloadFile = async (url, fallbackName) => {
    try {
      const r = await api.get(url, { params: { lang: es ? 'es' : 'en' }, responseType: 'blob' });
      const dispo = r.headers['content-disposition'] || '';
      const m = dispo.match(/filename="?([^";]+)"?/);
      const href = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = href; a.download = (m && m[1]) || fallbackName; a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) toast.error(t('talentexit.notAuthorized', { defaultValue: 'You are not authorized to view raw interview responses.' }));
      else if (code === 410) toast.error(t('talentexit.errors.retention_expired', { defaultValue: 'Retention period expired.' }));
      else toast.error(t('talentexit.export.failed', { defaultValue: 'Download failed.' }));
    }
  };

  const saveEvidence = () =>
    act(() => api.patch(`/client/talentexit/cases/${caseId}/evidence-link`, { url: evidenceUrl || null }),
        t('talentexit.evidence.saved', { defaultValue: 'Evidence link saved.' }));

  const viewResponses = async () => {
    try {
      const r = await api.get(`${base}/interview/responses`);
      setResponses(r.data); setShowResponses(true);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) toast.error(t('talentexit.interview.retentionExpired', { defaultValue: 'Retention period expired — raw responses are no longer available.' }));
      else if (code === 403) toast.error(t('talentexit.notAuthorized', { defaultValue: 'You are not authorized to view raw interview responses.' }));
      else toast.error(txError(t, e));
    }
  };

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat, items: (data.checklist || []).filter((i) => i.category === cat),
  })).filter((g) => g.items.length);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tx-case-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {data.snapshot?.user_name}
            <Badge variant="outline" className={closed ? 'bg-success/10 text-success border-success/20' : 'bg-info/10 text-info border-info/20'}>
              {t(`talentexit.status.${data.status}`, { defaultValue: data.status })}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {t(`talentexit.exitTypes.${data.exit_type}`, { defaultValue: data.exit_type })} · {t(`talentexit.reasons.${data.reason_category}`, { defaultValue: data.reason_category })}
            {data.snapshot?.department_name ? ` · ${data.snapshot.department_name}` : ''}
            {data.snapshot?.position ? ` · ${data.snapshot.position}` : ''}
          </DialogDescription>
        </DialogHeader>

        {/* ---- Interview ---- */}
        <section className="space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2"><MessageSquareText className="h-4 w-4" />{t('talentexit.interview.title', { defaultValue: 'Exit interview' })}</h4>
          <div className="flex items-center gap-2 flex-wrap" data-testid="tx-interview-panel">
            <Badge variant="outline">{t(`talentexit.interviewStatus.${iv.status || 'not_started'}`)}</Badge>
            {iv.status === 'waived' && iv.waive_note && <span className="text-xs text-muted-foreground">{iv.waive_note}</span>}
            {iv.status === 'recorded' && iv.recorded_by_name && (
              <span className="text-xs text-muted-foreground">{t('talentexit.interview.recordedBy', { defaultValue: 'Recorded by' })}: {iv.recorded_by_name}</span>
            )}
          </div>
          {iv.waived_at && iv.status !== 'waived' && (
            <p className="text-xs text-muted-foreground" data-testid="tx-previously-waived">
              {t('talentexit.interview.previouslyWaived', { defaultValue: 'Previously waived' })} · {format(new Date(iv.waived_at), es ? 'dd/MM/yyyy' : 'MM/dd/yyyy')}{iv.waive_note ? ` — ${iv.waive_note}` : ''}
            </p>
          )}
          {!adminMode && !closed && !['completed', 'recorded'].includes(iv.status) && (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="gap-1.5" disabled={busy || !!iv.assignment_id}
                onClick={() => act(() => api.post(`/client/talentexit/cases/${caseId}/interview/send`), t('talentexit.interview.sentOk', { defaultValue: 'Interview sent to the employee portal.' }))}
                data-testid="tx-send-interview">
                <Send className="h-3.5 w-3.5" />
                {iv.assignment_id ? t('talentexit.interview.alreadySent', { defaultValue: 'Sent — pending' }) : t('talentexit.interview.send', { defaultValue: 'Send portal interview' })}
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => setHrOpen(true)} data-testid="tx-record-interview">
                <PenLine className="h-3.5 w-3.5" /> {t('talentexit.interview.record', { defaultValue: 'Record HR interview' })}
              </Button>
              <Button size="sm" variant="ghost" className="gap-1.5 text-warning" disabled={busy} onClick={() => setWaiveOpen(true)} data-testid="tx-waive-interview">
                <CircleSlash className="h-3.5 w-3.5" /> {t('talentexit.interview.waive', { defaultValue: 'Waive (document reason)' })}
              </Button>
            </div>
          )}
          {['completed', 'recorded'].includes(iv.status) && adminMode && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="tx-admin-confidential-note">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              {t('talentexit.interview.hmConfidential', { defaultValue: 'Individual responses are confidential — visible only to the client\u2019s authorized HR users.' })}
            </p>
          )}
          {['completed', 'recorded'].includes(iv.status) && !adminMode && (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={viewResponses} data-testid="tx-view-responses">
                {data.caller_is_authorized === false ? <Lock className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {t('talentexit.interview.viewResponses', { defaultValue: 'View responses' })}
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => downloadFile(`${base}/interview/export/pdf`, 'entrevista-salida.pdf')} data-testid="tx-export-pdf">
                <Download className="h-3.5 w-3.5" /> PDF
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => downloadFile(`${base}/interview/export/docx`, 'entrevista-salida.docx')} data-testid="tx-export-docx">
                <Download className="h-3.5 w-3.5" /> Word
              </Button>
            </div>
          )}
        </section>

        <Separator />

        {/* ---- Checklist ---- */}
        <section className="space-y-3">
          <h4 className="text-sm font-semibold">{t('talentexit.checklist.title', { defaultValue: 'Handover & closure checklist' })}</h4>
          {!adminMode && (
            <Button size="sm" variant="outline" className="gap-1.5"
              onClick={() => downloadFile(`/client/talentexit/cases/${caseId}/handover-template`, 'acta-entrega-cierre.docx')}
              data-testid="tx-handover-template">
              <Download className="h-3.5 w-3.5" /> {t('talentexit.handover.download', { defaultValue: 'Download Handover & Closure Form' })}
            </Button>
          )}
          {grouped.map((g) => (
            <div key={g.cat} className="space-y-1.5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{t(`talentexit.checklist.categories.${g.cat}`, { defaultValue: g.cat })}</p>
              {g.items.map((item) => (
                <div key={item.id} className="flex items-start justify-between gap-3 rounded border border-border/60 px-3 py-2" data-testid={`tx-check-item-${item.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm">{es ? item.label_es : item.label_en}{item.required && <span className="text-destructive"> *</span>}</p>
                    {item.note && <p className="text-xs text-muted-foreground mt-0.5">{item.note}</p>}
                    {item.resolved_by_name && (
                      <p className="text-xs text-muted-foreground mt-0.5">{item.resolved_by_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {item.status === 'completed' && <Badge variant="outline" className="bg-success/10 text-success border-success/20">{t('talentexit.checklist.completed', { defaultValue: 'Done' })}</Badge>}
                    {item.status === 'not_applicable' && <Badge variant="outline" className="bg-muted text-muted-foreground">{t('talentexit.checklist.na', { defaultValue: 'N/A' })}</Badge>}
                    {!adminMode && !closed && item.status === 'pending' && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 px-2 gap-1" disabled={busy}
                          onClick={() => patchItem(item, 'completed')} data-testid={`tx-check-done-${item.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> {t('talentexit.checklist.markDone', { defaultValue: 'Done' })}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2" disabled={busy}
                          onClick={() => setNaItem(item)} data-testid={`tx-check-na-${item.id}`}>
                          {t('talentexit.checklist.markNa', { defaultValue: 'N/A' })}
                        </Button>
                      </>
                    )}
                    {!adminMode && !closed && item.status !== 'pending' && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground" disabled={busy}
                        onClick={() => patchItem(item, 'pending')}>
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>

        <Separator />

        {/* ---- Closure ---- */}
        <section className="space-y-3">
          <h4 className="text-sm font-semibold">{t('talentexit.closure.title', { defaultValue: 'Closure' })}</h4>
          {closed ? (
            <div className="space-y-1.5 text-sm" data-testid="tx-closed-info">
              <p className="text-success flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />
                {t('talentexit.closure.closedAt', { defaultValue: 'Closed' })} · {data.closed_by_name || ''} · {data.closed_at ? format(new Date(data.closed_at), es ? 'dd/MM/yyyy' : 'MM/dd/yyyy') : ''}</p>
              {data.closure_note && <p className="text-muted-foreground">{data.closure_note}</p>}
              {data.retention_until && (
                <p className="text-xs text-muted-foreground">{t('talentexit.closure.retentionUntil', { defaultValue: 'Raw interview readable until' })}: {format(new Date(data.retention_until), es ? 'dd/MM/yyyy' : 'MM/dd/yyyy')}</p>
              )}
              {!adminMode && (
                <Button size="sm" variant="outline" className="gap-1.5 mt-1" onClick={() => setReopenOpen(true)} data-testid="tx-reopen-btn">
                  <RotateCcw className="h-3.5 w-3.5" /> {t('talentexit.closure.reopen', { defaultValue: 'Reopen case' })}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {(data.closure_blockers || []).length > 0 && (
                <div className="rounded border border-warning/40 bg-warning/5 p-3 text-sm space-y-1" data-testid="tx-closure-blockers">
                  <p className="font-medium text-warning">{t('talentexit.closure.blocked', { defaultValue: 'Cannot close yet:' })}</p>
                  <ul className="list-disc pl-5 text-muted-foreground">
                    {(data.closure_blockers || []).map((b, i) => (
                      <li key={i}>
                        {b.kind === 'interview_unresolved'
                          ? t('talentexit.closure.interviewUnresolved', { defaultValue: 'Exit interview not completed, recorded or waived.' })
                          : `${t('talentexit.closure.itemPending', { defaultValue: 'Required checklist item pending' })}: ${es ? b.label_es : b.label_en}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!adminMode && (
                <>
                  <Label>{t('talentexit.closure.note', { defaultValue: 'Closure note (optional)' })}</Label>
                  <Textarea rows={2} value={closureNote} onChange={(e) => setClosureNote(e.target.value)} data-testid="tx-closure-note" />
                  <Button disabled={busy || (data.closure_blockers || []).length > 0} className="gap-1.5"
                    onClick={() => act(() => api.post(`/client/talentexit/cases/${caseId}/close`, { closure_note: closureNote || null }), t('talentexit.closure.closedOk', { defaultValue: 'Exit case closed.' }))}
                    data-testid="tx-close-btn">
                    <CheckCircle2 className="h-4 w-4" /> {t('talentexit.closure.close', { defaultValue: 'Close case' })}
                  </Button>
                </>
              )}
            </div>
          )}
          {(data.reopen_history || []).length > 0 && (
            <div className="text-xs text-muted-foreground space-y-0.5">
              {(data.reopen_history || []).map((r, i) => (
                <p key={i}>{t('talentexit.closure.reopened', { defaultValue: 'Reopened' })} · {r.by_name} · {format(new Date(r.at), es ? 'dd/MM/yyyy' : 'MM/dd/yyyy')} — {r.note}</p>
              ))}
            </div>
          )}
        </section>

        <Separator />

        {/* ---- External closure evidence link (TE-UAT-08B) ---- */}
        <section className="space-y-2">
          <h4 className="text-sm font-semibold flex items-center gap-2"><LinkIcon className="h-4 w-4" />{t('talentexit.evidence.label', { defaultValue: 'Closure evidence link' })}</h4>
          <p className="text-xs text-muted-foreground">{t('talentexit.evidence.hint', { defaultValue: 'CTH stores only the link to the signed handover document kept in your own repository (SharePoint, Drive, DMS…). Changes are audited.' })}</p>
          <div className="flex flex-col sm:flex-row gap-2 min-w-0">
            <Input value={evidenceUrl} disabled={adminMode} placeholder="https://…"
              onChange={(e) => setEvidenceUrl(e.target.value)}
              className="flex-1 min-w-0" data-testid="tx-evidence-input" />
            {!adminMode && (
              <Button size="sm" variant="outline" className="shrink-0" disabled={busy} onClick={saveEvidence} data-testid="tx-evidence-save">
                {t('common.save', { defaultValue: 'Save' })}
              </Button>
            )}
            {data.evidence_link?.url && (
              <Button asChild size="sm" variant="ghost" className="shrink-0 gap-1.5">
                <a href={data.evidence_link.url} target="_blank" rel="noopener noreferrer" data-testid="tx-evidence-open">
                  <ExternalLink className="h-3.5 w-3.5" /> {t('talentexit.evidence.open', { defaultValue: 'Open' })}
                </a>
              </Button>
            )}
          </div>
          {data.evidence_link?.set_by_name && (
            <p className="text-xs text-muted-foreground">{data.evidence_link.set_by_name} · {format(new Date(data.evidence_link.set_at), es ? 'dd/MM/yyyy' : 'MM/dd/yyyy')}</p>
          )}
        </section>

        <Separator />

        {/* ---- Case history (TE-UAT-07) ---- */}
        {history.length > 0 && (
          <section className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2"><History className="h-4 w-4" />{t('talentexit.history.title', { defaultValue: 'Case history' })}</h4>
            <div className="space-y-1.5" data-testid="tx-case-history">
              {history.map((ev, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/60 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-medium">{t(`talentexit.history.events.${ev.event}`, { defaultValue: ev.event })}</span>
                    <span className="text-muted-foreground"> · {ev.actor || '—'} · {ev.at ? format(new Date(ev.at), es ? 'dd/MM/yyyy HH:mm' : 'MM/dd/yyyy HH:mm') : ''}</span>
                    {ev.note && <p className="text-muted-foreground truncate" title={ev.note}>{ev.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <NoteDialog open={waiveOpen} onOpenChange={setWaiveOpen}
          title={t('talentexit.interview.waiveTitle', { defaultValue: 'Waive exit interview' })}
          description={t('talentexit.interview.waiveDesc', { defaultValue: 'Document why the interview will not take place (e.g. the employee declined).' })}
          submitLabel={t('talentexit.interview.waiveSubmit', { defaultValue: 'Waive' })}
          onSubmit={(note) => act(() => api.post(`/client/talentexit/cases/${caseId}/interview/waive`, { note }))}
          testId="tx-waive-dialog" />
        <NoteDialog open={reopenOpen} onOpenChange={setReopenOpen}
          title={t('talentexit.closure.reopenTitle', { defaultValue: 'Reopen exit case' })}
          description={t('talentexit.closure.reopenDesc', { defaultValue: 'Reopening is audited. Explain why.' })}
          submitLabel={t('talentexit.closure.reopen', { defaultValue: 'Reopen case' })}
          onSubmit={(note) => act(() => api.post(`/client/talentexit/cases/${caseId}/reopen`, { note }))}
          testId="tx-reopen-dialog" />
        <NoteDialog open={!!naItem} onOpenChange={(o) => { if (!o) setNaItem(null); }}
          title={t('talentexit.checklist.naTitle', { defaultValue: 'Mark as not applicable' })}
          description={t('talentexit.checklist.naDesc', { defaultValue: 'A justification note is required.' })}
          submitLabel={t('talentexit.checklist.markNa', { defaultValue: 'N/A' })}
          onSubmit={(note) => patchItem(naItem, 'not_applicable', note)}
          testId="tx-na-dialog" />
        {hrOpen && <HrRecordDialog open={hrOpen} onOpenChange={setHrOpen} caseId={caseId} onDone={load} />}

        <Dialog open={showResponses} onOpenChange={setShowResponses}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('talentexit.interview.responsesTitle', { defaultValue: 'Exit interview responses' })}</DialogTitle>
              <DialogDescription>{t('talentexit.interview.responsesDesc', { defaultValue: 'Confidential — access to this record is audited.' })}</DialogDescription>
            </DialogHeader>
            {/* S2 — original completion timestamp (persisted, never altered) */}
            {(responses?.completed_at || data?.interview?.completed_at) && (
              <p className="text-xs text-muted-foreground -mt-1" data-testid="tx-interview-completed-at">
                {t('talentexit.interview.completedAt', { defaultValue: 'Fecha de finalización de la entrevista' })}:{' '}
                <b>{new Date(responses?.completed_at || data.interview.completed_at).toLocaleString()}</b>
              </p>
            )}
            <TalentExitResponsesView data={responses} />
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
